import React, { useState, useEffect } from 'react';
import { getReport, downloadMarkdown, downloadPdf, downloadExport } from '../../services/cardAnalysisService';
import { CardBatchExportFormat } from '../../types';
import { CardAnalysisReportWithData } from '../../types';
import ReactMarkdown from 'react-markdown';

interface BatchReportDisplayProps {
  reportIds: string[];
  onStartOver: () => void;
}

const BatchReportDisplay: React.FC<BatchReportDisplayProps> = ({ reportIds, onStartOver }) => {
  const [reports, setReports] = useState<CardAnalysisReportWithData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeReportIndex, setActiveReportIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState<string | null>(null);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const fetchedReports = await Promise.all(
          reportIds.map(id => getReport(id))
        );
        
        setReports(fetchedReports);
      } catch (err) {
        setError(`Error fetching reports: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };
    
    if (reportIds.length > 0) {
      fetchReports();
    }
  }, [reportIds]);

  const handleDownloadMarkdown = async () => {
    if (reports.length === 0 || activeReportIndex >= reports.length) return;
    
    try {
      setIsDownloading(true);
      setDownloadType('markdown');
      
      const report = reports[activeReportIndex];
      await downloadMarkdown(report.report_content, `report-${report.id}.md`);
    } catch (err) {
      setError(`Error downloading markdown: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDownloading(false);
      setDownloadType(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (reports.length === 0 || activeReportIndex >= reports.length) return;
    
    try {
      setIsDownloading(true);
      setDownloadType('pdf');
      
      const report = reports[activeReportIndex];
      await downloadPdf(report.report_content, `report-${report.id}.pdf`);
    } catch (err) {
      setError(`Error downloading PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDownloading(false);
      setDownloadType(null);
    }
  };

  const handleExportBatch = async (format: CardBatchExportFormat) => {
    if (reports.length === 0) return;
    
    try {
      setIsDownloading(true);
      setDownloadType(format);
      
      const cardIds = reports.map(report => report.card_id);
      await downloadExport(cardIds, format, `batch-export.${format}`);
    } catch (err) {
      setError(`Error exporting as ${format}: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDownloading(false);
      setDownloadType(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Loading Reports...</h2>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Error</h2>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button 
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          onClick={onStartOver}
        >
          Start Over
        </button>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">No Reports Available</h2>
        <p className="mb-4">No reports were found for the analyzed cards.</p>
        <button 
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          onClick={onStartOver}
        >
          Start Over
        </button>
      </div>
    );
  }

  const activeReport = reports[activeReportIndex];
  const structuredData = activeReport.report_data?.structured_data;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Business Card Analysis Reports</h2>
      
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-medium">Report {activeReportIndex + 1} of {reports.length}</h3>
          <div className="flex space-x-2">
            <button 
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
              onClick={() => setActiveReportIndex(prev => Math.max(0, prev - 1))}
              disabled={activeReportIndex === 0}
            >
              Previous
            </button>
            <button 
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
              onClick={() => setActiveReportIndex(prev => Math.min(reports.length - 1, prev + 1))}
              disabled={activeReportIndex === reports.length - 1}
            >
              Next
            </button>
          </div>
        </div>
        
        <div className="text-sm text-gray-500">
          Report ID: {activeReport.id} | Generated: {new Date(activeReport.created_at).toLocaleString()}
        </div>
      </div>
      
      {structuredData && (
        <div className="mb-6 border rounded-lg p-4 bg-gray-50">
          <h3 className="text-lg font-medium mb-2">Structured Data</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-semibold">Contact Name:</p>
              <p>{structuredData.contact_name || 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Position:</p>
              <p>{structuredData.position || 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Company:</p>
              <p>{structuredData.company_name || 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Phone:</p>
              <p>{structuredData.phone || 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Email:</p>
              <p>{structuredData.email || 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Website:</p>
              <p>{structuredData.company_website || 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Address:</p>
              <p>{structuredData.address || 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Country:</p>
              <p>{structuredData.country || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}
      
      {activeReport.report_data?.personal_report && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Personal Report</h3>
          <div className="border rounded-lg p-4 bg-gray-50 prose max-w-none">
            <ReactMarkdown>{activeReport.report_data.personal_report}</ReactMarkdown>
          </div>
        </div>
      )}
      
      {activeReport.report_data?.company_report && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Company Report</h3>
          <div className="border rounded-lg p-4 bg-gray-50 prose max-w-none">
            <ReactMarkdown>{activeReport.report_data.company_report}</ReactMarkdown>
          </div>
        </div>
      )}
      
      <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4 mb-6">
        <div className="flex-1">
          <h3 className="text-lg font-medium mb-2">Single Report Actions</h3>
          <div className="flex space-x-2">
            <button 
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center disabled:opacity-50"
              onClick={handleDownloadMarkdown}
              disabled={isDownloading}
            >
              {isDownloading && downloadType === 'markdown' ? (
                <span className="mr-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              ) : null}
              Download Markdown
            </button>
            {/* 
            <button 
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center disabled:opacity-50"
              onClick={handleDownloadPdf}
              disabled={isDownloading}
            >
              {isDownloading && downloadType === 'pdf' ? (
                <span className="mr-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              ) : null}
              Download PDF
            </button>
            */}
          </div>
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-medium mb-2">Batch Export</h3>
          <div className="flex space-x-2">
            <button 
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center disabled:opacity-50"
              onClick={() => handleExportBatch(CardBatchExportFormat.CSV)}
              disabled={isDownloading}
            >
              {isDownloading && downloadType === CardBatchExportFormat.CSV ? (
                <span className="mr-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              ) : null}
              Export CSV
            </button>
            <button 
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center disabled:opacity-50"
              onClick={() => handleExportBatch(CardBatchExportFormat.XLSX)}
              disabled={isDownloading}
            >
              {isDownloading && downloadType === CardBatchExportFormat.XLSX ? (
                <span className="mr-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              ) : null}
              Export XLSX
            </button>
            {/*  
            <button 
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center disabled:opacity-50"
              onClick={() => handleExportBatch(CardBatchExportFormat.PDF)}
              disabled={isDownloading}
            >
              {isDownloading && downloadType === CardBatchExportFormat.PDF ? (
                <span className="mr-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              ) : null}
              Export PDF
            </button>
            */}
          </div>
        </div>
      </div>
      
      <div className="flex justify-center">
        <button 
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          onClick={onStartOver}
        >
          Start Over
        </button>
      </div>
    </div>
  );
};

export default BatchReportDisplay;
